import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { InvoiceEntity } from './invoice.entity';
import { ItemEntity } from './item.entity';
import { OrderEntity } from './order.entity';

@Index('client_item__fk_invoice__id_fk', ['fkInvoiceId'], {})
@Index('client_item__fk_item__id_fk', ['fkItemId'], {})
@Index('client_item__fk_order__id_fk', ['fkOrderId'], {})
@Index(
	'client_item_hasAggregatedData__deleted_userId',
	['deletedUserId', 'hasAggregatedData'],
	{},
)
@Index('client_item_pk', ['fkClientId', 'fkItemId'], {
	unique: true,
})
@Index(
	'client_item_price_trend_index',
	['lastPrice', 'fkClientId', 'fkItemId', 'minimumTrendDiff'],
	{},
)
@Index('fk_invoice__id_last', ['fkInvoiceIdLast'], {})
@Index('fk_order__id_last', ['fkOrderIdLast'], {})
// PD-17502: one customer item number means one item for that customer. NULLs
// are distinct in a MySQL unique index, so rows without a number never collide.
@Index(
	'client_item_client_item_number',
	['fkClientId', 'clientItemNumberNormalized'],
	{ unique: true },
)
// PD-17504: the Cross-References list reads the numbers of every customer, so
// the per-customer index above cannot serve it; without this it reads every
// client_item row (184,104 on Macomb staging, 2 of them numbered, 2026-10-25).
@Index('client_item_number_normalized', ['clientItemNumberNormalized'])
// PD-17502: undoing a quote-request confirmation finds the number it created.
@Index('client_item_number_request_line', [
	'fkCrossReferenceRequestLineIdClientItemNumber',
])
@Entity('client_item')
export class ClientItemEntity extends BaseEntity {
	@Column('decimal', {
		name: 'lastPrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public lastPrice!: string | null;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', { name: '_fk_item__id', unsigned: true })
	public fkItemId!: number;

	@Column('int', { name: '_fk_invoice__id', nullable: true, unsigned: true })
	public fkInvoiceId!: number | null;

	@Column('int', { name: '_fk_order__id', nullable: true, unsigned: true })
	public fkOrderId!: number | null;

	@Column('int', { name: 'soldUnitsMtd', nullable: true })
	public soldUnitsMtd!: number | null;

	@Column('int', { name: 'soldUnitsLastMonth', nullable: true })
	public soldUnitsLastMonth!: number | null;

	@Column('int', { name: 'soldUnitsTwoMonthsAgo', nullable: true })
	public soldUnitsTwoMonthsAgo!: number | null;

	@Column('decimal', {
		name: 'revenueMtd',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public revenueMtd!: string | null;

	@Column('decimal', {
		name: 'revenueLastMonth',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public revenueLastMonth!: string | null;

	@Column('decimal', {
		name: 'revenueTwoMonthsAgo',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public revenueTwoMonthsAgo!: string | null;

	@Column('int', {
		name: 'fk_invoice__id_last',
		nullable: true,
		unsigned: true,
	})
	public fkInvoiceIdLast!: number | null;

	@Column('int', { name: 'fk_order__id_last', nullable: true, unsigned: true })
	public fkOrderIdLast!: number | null;

	@Column('int', { name: 'dailyLimit', nullable: true })
	public dailyLimit!: number | null;

	// Per-client override of item.monthlyLimit (rolling 30-day window, PD-14038).
	// Synced from NetSuite customrecord_mhi_customeritemmax for pharmsource.
	@Column('int', { name: 'monthlyLimit', nullable: true })
	public monthlyLimit!: number | null;

	@Column('decimal', {
		name: 'averageInvoiceRevenue',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public averageInvoiceRevenue!: string | null;

	@Column('decimal', {
		name: 'invoiceFrequency',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public invoiceFrequency!: string | null;

	@Column('decimal', {
		name: 'forecast',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public forecast!: string | null;

	@Column('decimal', {
		name: 'likelihood',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public likelihood!: string | null;

	@Column('decimal', {
		name: 'revenueTrend360',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrend360!: string | null;

	@Column('decimal', {
		name: 'revenueTrendDiff360',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrendDiff360!: string | null;

	@Column('decimal', {
		name: 'revenueTrend90',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrend90!: string | null;

	@Column('decimal', {
		name: 'revenueTrendDiff90',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrendDiff90!: string | null;

	@Column('decimal', {
		name: 'minimumTrendDiff',
		precision: 14,
		scale: 10,
		default: () => "'0.0000000000'",
	})
	public minimumTrendDiff!: string;

	@Column('decimal', {
		name: 'rollingTotalGPAggregate30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregate30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregate90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregate90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMargin30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMargin30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMargin90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMargin90!: string | null;

	@Column('decimal', {
		name: 'rollingRevenue30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenue30!: string | null;

	@Column('decimal', {
		name: 'rollingRevenue90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenue90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregateTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregateTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregateTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregateTrend90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMarginTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMarginTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMarginTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMarginTrend90!: string | null;

	@Column('decimal', {
		name: 'rollingRevenueTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenueTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingRevenueTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenueTrend90!: string | null;

	@Column('decimal', {
		name: 'rollingQuantity30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingQuantity30!: string | null;

	@Column('decimal', {
		name: 'rollingQuantity90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingQuantity90!: string | null;

	@Column('decimal', {
		name: 'averageInvoiceTotalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public averageInvoiceTotalGp!: string | null;

	@Column('decimal', {
		name: 'averageInvoiceQuantity',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public averageInvoiceQuantity!: string | null;

	@Column('date', { name: 'forecastedReorderDate', nullable: true })
	public forecastedReorderDate!: string | null;

	@Column('int', { name: 'frequencyRank', nullable: true, unsigned: true })
	public frequencyRank!: number | null;

	@Column('int', { name: 'revenueRank', nullable: true, unsigned: true })
	public revenueRank!: number | null;

	@Column('int', { name: 'totalGPRank', nullable: true, unsigned: true })
	public totalGpRank!: number | null;

	@Column('int', { name: 'quantityRank', nullable: true, unsigned: true })
	public quantityRank!: number | null;

	@Column('int', { name: 'revenueReorderRank', nullable: true, unsigned: true })
	public revenueReorderRank!: number | null;

	@Column('int', { name: 'totalGPReorderRank', nullable: true, unsigned: true })
	public totalGpReorderRank!: number | null;

	@Column('decimal', {
		name: 'lastOrderedListPrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public lastOrderedListPrice!: string | null;

	@Column({
		type: 'boolean',
		name: 'hasAggregatedData',
		width: 1,
		default: () => "'0'",
	})
	public hasAggregatedData!: boolean;

	@Column({
		type: 'boolean',
		name: 'favorite',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public favorite!: boolean | null;

	/**
	 * PD-17502 — the customer's own number for this item, as they write it
	 * ("LMB-2BLK90"). Set by a person (the client's Item Numbers tab, an
	 * import, a confirmed quote request match) or the ERP, never by the sales
	 * jobs that write the columns above, which update only what they compute.
	 */
	@Column('varchar', { name: 'clientItemNumber', nullable: true, length: 255 })
	public clientItemNumber!: string | null;

	/**
	 * The number folded for comparison (`normalizeIdentifier`: case, spaces and
	 * punctuation removed), so "lmb 2blk-90" finds it. Unique per customer.
	 */
	@Column('varchar', {
		name: 'clientItemNumberNormalized',
		nullable: true,
		length: 255,
	})
	public clientItemNumberNormalized!: string | null;

	/** The customer's description of the item, from their list. */
	@Column('varchar', {
		name: 'clientItemDescription',
		nullable: true,
		length: 500,
	})
	public clientItemDescription!: string | null;

	/**
	 * How the number was recorded: 'review' (confirmed on a quote request),
	 * 'manual' (entered by a person), 'import' (the customer's uploaded list) or
	 * 'erp'. Named for the number because the row's own audit columns describe
	 * the row, which the sales jobs create long before any number exists.
	 */
	@Column('varchar', {
		name: 'clientItemNumberSource',
		nullable: true,
		length: 32,
	})
	public clientItemNumberSource!: string | null;

	/**
	 * Who recorded or confirmed the number: the tenant's own user id
	 * (`user._id`), as `item_cross_reference.confirmedByUserId` records it.
	 */
	@Column('int', {
		name: 'clientItemNumberConfirmedByUserId',
		nullable: true,
		unsigned: true,
	})
	public clientItemNumberConfirmedByUserId!: number | null;

	/** When the number was recorded or confirmed. */
	@Column('datetime', { name: 'clientItemNumberConfirmedAt', nullable: true })
	public clientItemNumberConfirmedAt!: Date | null;

	/**
	 * The quote request line whose confirmation recorded the number. Undoing that
	 * confirmation clears the number it created, and only that one.
	 */
	@Column('int', {
		name: 'fk_cross_reference_request_line__id_clientItemNumber',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceRequestLineIdClientItemNumber!: number | null;

	@ManyToOne(() => InvoiceEntity, (invoice) => invoice.clientItems, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_invoice__id_last', referencedColumnName: 'id' }])
	public fkInvoiceIdLast2!: InvoiceEntity;

	@ManyToOne(() => OrderEntity, (order) => order.clientItems, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_order__id_last', referencedColumnName: 'id' }])
	public fkOrderIdLast2!: OrderEntity;

	@ManyToOne(() => ClientEntity, (client) => client.clientItems, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(() => InvoiceEntity, (invoice) => invoice.clientItems2, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity;

	@ManyToOne(() => ItemEntity, (item) => item.clientItems, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_item__id', referencedColumnName: 'id' }])
	public fkItem!: ItemEntity;

	@ManyToOne(() => OrderEntity, (order) => order.clientItems2, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_order__id', referencedColumnName: 'id' }])
	public fkOrder!: OrderEntity;
}
